import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Search, X } from 'lucide-react';
import type {
  OverrideDailyLimitResult,
  OverrideRejectedOrder,
  PickupOrdersLookup,
  PickupSlotCode,
} from '../../services/pickupSlots.api';
import {
  OVERRIDE_REASON_MAX,
  getPickupOrders,
  overrideDailyLimit,
} from '../../services/pickupSlots.api';
import { searchMerchants, type Merchant } from '../../services/merchants.api';

interface Props {
  onClose: () => void;
}

/** Core's machine reasons, in officer language. Unknown codes fall through to core's own message. */
const REJECT_REASON_LABELS: Record<string, string> = {
  attached_to_active_pickup: 'already in another active pickup',
  belongs_to_other_merchant: 'belongs to a different merchant',
  not_found: 'not found in core',
  not_ready: 'not marked ready for pickup',
  cancelled: 'cancelled',
};

function RejectedList({
  orders,
  tone,
  heading,
}: {
  orders: OverrideRejectedOrder[];
  tone: 'error' | 'warning';
  heading: string;
}) {
  const shell = tone === 'error' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900';
  return (
    <div className={`rounded-md p-3 text-xs ${shell}`}>
      <p className="font-medium">{heading}</p>
      <ul className="mt-1 space-y-1">
        {orders.map((o) => (
          <li key={o.uid}>
            <span className="font-mono">{o.uid.slice(-8)}</span>
            {' — '}
            {(o.reason && REJECT_REASON_LABELS[o.reason]) ?? o.message ?? o.reason ?? 'rejected'}
            {o.pick_up_platform_code && (
              <span className="ml-1 font-mono opacity-80">({o.pick_up_platform_code})</span>
            )}
            {o.status_slug && <span className="ml-1 opacity-70">· status {o.status_slug}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Fleet-officer escape hatch: force a fresh pickup despite the one-per-day rule. */
export default function OverrideDailyLimitModal({ onClose }: Props) {
  // Merchant — picked from a searchable dropdown; only the uid goes to the API.
  const [merchantQuery, setMerchantQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [merchant, setMerchant] = useState<{ uid: string; name: string } | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const merchantBoxRef = useRef<HTMLDivElement>(null);

  // Orders — resolved from the task code on the Tasks page (BXT-…), or the
  // pickup's own BXPU-… code; selectable, with a manual-UIDs fallback behind a toggle.
  const [platformCode, setPlatformCode] = useState('');
  const [lookup, setLookup] = useState<PickupOrdersLookup | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [orderUidsRaw, setOrderUidsRaw] = useState('');

  const [slotCode, setSlotCode] = useState<PickupSlotCode | ''>('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Core names a reason per order it refused ("attached_to_active_pickup",
  // "belongs_to_other_merchant", …). Showing only the generic top-line message
  // is what made an attachment rule look like a broken daily-limit bypass.
  const [rejected, setRejected] = useState<OverrideRejectedOrder[]>([]);
  const [result, setResult] = useState<OverrideDailyLimitResult | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(merchantQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [merchantQuery]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (merchantBoxRef.current && !merchantBoxRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [dropdownOpen]);

  const merchantResults = useQuery<Merchant[]>({
    queryKey: ['merchantSearch', debouncedQuery],
    queryFn: () => searchMerchants(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 60_000,
  });

  const lookupMutation = useMutation({
    mutationFn: () => getPickupOrders(platformCode),
    onSuccess: (data) => {
      setLookup(data);
      setLookupError(null);
      // Everything still active is what the officer almost always wants.
      setSelectedOrders(new Set(data.orders.filter((o) => !o.cancelled).map((o) => o.uid)));
      // The code identifies the merchant too — fill it unless one is already picked.
      if (!merchant && data.merchant) setMerchant(data.merchant);
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      setLookup(null);
      setSelectedOrders(new Set());
      setLookupError(err.response?.data?.error ?? 'Lookup failed');
    },
  });

  const orderUids = manualMode
    ? orderUidsRaw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
    : [...selectedOrders];

  const mutation = useMutation({
    mutationFn: () =>
      overrideDailyLimit({
        merchant_uid: merchant!.uid,
        order_uids: orderUids,
        ...(slotCode ? { time_slot_code: slotCode } : {}),
        reason: reason.trim(),
        // Always. A merchant's orders auto-attach to the day's task, so the
        // normal case is "attached", which core refuses without this — and an
        // unattached order makes force a no-op. There is no case where an
        // officer opens this modal and wants the call to fail on attachment.
        force: true,
      }),
    onSuccess: (data) => {
      setResult(data);
      setRejected(data.rejected);
      setError(null);
    },
    onError: (err: { response?: { data?: { error?: string; rejected?: OverrideRejectedOrder[] } } }) => {
      setError(err.response?.data?.error ?? 'Override failed');
      setRejected(err.response?.data?.rejected ?? []);
    },
  });

  const done = result !== null;
  // Core pushed the pickup to tomorrow because the slot had already closed —
  // the officer needs to know it is NOT on today's board.
  const nextDay = result?.pickups.find((p) => p.slot_state === 'next_day_fallback') ?? null;
  const emptiedSource = (result?.source_pickups ?? []).find((p) => p.remaining_order_count === 0);
  // Pre-submit: these orders are still in a live pickup, so this will MOVE them.
  const willMove = !manualMode && lookup !== null && !lookup.pickup.cancelled && orderUids.length > 0;

  const valid =
    merchant && orderUids.length > 0 && reason.trim() && reason.length <= OVERRIDE_REASON_MAX;

  const toggleOrder = (uid: string) => {
    setSelectedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">Override daily pickup limit</h2>
        <p className="mt-1 text-sm text-gray-500">
          Creates a FRESH pickup for a merchant who already has one today (never merged).
          The override is audit-logged with your reason.
        </p>
        {done ? (
          <div className="mt-3 space-y-2">
            <div className="rounded-md bg-green-50 p-3 text-sm text-green-800">
              <p className="font-medium">
                New pickup created
                {result?.pickups[0]?.platform_code && (
                  <span className="ml-1 font-mono text-xs">{result.pickups[0].platform_code}</span>
                )}
                .
              </p>
              <p className="mt-0.5 text-xs">It appears on the board after the next feed sync.</p>
            </div>
            {nextDay && (
              <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">Scheduled for {nextDay.operational_date}, not today.</p>
                <p className="mt-0.5 text-xs">
                  The slot you picked had already closed, so core moved the pickup to the next
                  operational day. It will not show on today's board.
                </p>
              </div>
            )}
            {(result?.source_pickups ?? []).length > 0 && (
              <div className="rounded-md border border-gray-200 p-3 text-xs text-gray-600">
                <p>
                  Orders were moved out of{' '}
                  {result!.source_pickups
                    .map((p) => p.platform_code ?? p.uid)
                    .filter(Boolean)
                    .join(', ')}
                  .
                </p>
                {emptiedSource && (
                  <p className="mt-1 font-medium text-amber-700">
                    {emptiedSource.platform_code ?? 'The source pickup'} now holds 0 orders — cancel it
                    if it is no longer needed, or the driver will be sent to an empty pickup.
                  </p>
                )}
              </div>
            )}
            {rejected.length > 0 && (
              <RejectedList orders={rejected} tone="warning" heading="Not every order was moved:" />
            )}
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <div ref={merchantBoxRef} className="relative">
              <label className="block text-xs font-medium text-gray-600">Merchant</label>
              {merchant ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-blue-900">{merchant.name || '(no name)'}</p>
                    <p className="truncate font-mono text-[10px] text-blue-700">{merchant.uid}</p>
                  </div>
                  <button
                    onClick={() => { setMerchant(null); setMerchantQuery(''); }}
                    className="ml-2 rounded p-1 text-blue-700 hover:bg-blue-100"
                    title="Clear merchant"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative mt-1">
                    <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-gray-400" />
                    <input
                      value={merchantQuery}
                      onChange={(e) => { setMerchantQuery(e.target.value); setDropdownOpen(true); }}
                      onFocus={() => setDropdownOpen(true)}
                      placeholder="Search merchant by name…"
                      className="w-full rounded-md border border-gray-300 py-1.5 pl-7 pr-2 text-sm"
                    />
                  </div>
                  {dropdownOpen && debouncedQuery.length >= 2 && (
                    <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                      {merchantResults.isLoading ? (
                        <p className="px-3 py-2 text-sm text-gray-500">Searching…</p>
                      ) : (merchantResults.data ?? []).length === 0 ? (
                        <p className="px-3 py-2 text-sm text-gray-500">No merchants match "{debouncedQuery}"</p>
                      ) : (
                        (merchantResults.data ?? []).map((m) => (
                          <button
                            key={m.uid}
                            onClick={() => {
                              setMerchant({ uid: m.uid, name: m.full_name });
                              setDropdownOpen(false);
                            }}
                            className="block w-full px-3 py-1.5 text-left hover:bg-blue-50"
                          >
                            <span className="block truncate text-sm text-gray-900">{m.full_name}</span>
                            <span className="block truncate font-mono text-[10px] text-gray-500">{m.uid}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="block text-xs font-medium text-gray-600">
                  {manualMode ? 'Order UIDs (comma/space separated, min 1)' : 'Orders from a task code'}
                </label>
                <button
                  onClick={() => {
                    setManualMode(!manualMode);
                    // clear the rejections too: they name orders from the mode
                    // being left, so keeping them would blame the wrong uids
                    setError(null);
                    setRejected([]);
                  }}
                  className="text-[11px] text-blue-600 hover:underline"
                >
                  {manualMode ? 'Use task code instead' : 'Enter order UIDs manually'}
                </button>
              </div>
              {manualMode ? (
                <textarea
                  value={orderUidsRaw}
                  onChange={(e) => setOrderUidsRaw(e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                />
              ) : (
                <>
                  <div className="mt-1 flex gap-2">
                    <input
                      value={platformCode}
                      onChange={(e) => setPlatformCode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && platformCode.trim()) lookupMutation.mutate();
                      }}
                      placeholder="BXT-262083517959"
                      className="w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-sm"
                    />
                    <button
                      onClick={() => lookupMutation.mutate()}
                      disabled={!platformCode.trim() || lookupMutation.isPending}
                      className="whitespace-nowrap rounded-md border border-blue-600 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                    >
                      {lookupMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Get orders'}
                    </button>
                  </div>
                  <p className="mt-1 text-[11px] text-gray-500">
                    The <span className="font-mono">BXT-…</span> code from the Tasks page. The pickup's own{' '}
                    <span className="font-mono">BXPU-…</span> code works too.
                  </p>
                  {lookupError && <p className="mt-1 text-sm text-red-600">{lookupError}</p>}
                  {lookup && (
                    <div className="mt-2 rounded-md border border-gray-200">
                      <div className="flex items-center justify-between border-b border-gray-100 px-2 py-1.5">
                        <p className="text-xs text-gray-600">
                          {lookup.orders.length} order{lookup.orders.length === 1 ? '' : 's'} in pickup{' '}
                          <span className="font-mono">{lookup.pickup.platform_code}</span>
                          {lookup.pickup.cancelled && (
                            <span className="ml-1 rounded bg-red-100 px-1 py-0.5 text-[10px] font-medium text-red-700">
                              cancelled pickup
                            </span>
                          )}
                        </p>
                        <p className="text-xs font-medium text-blue-700">{selectedOrders.size} selected</p>
                      </div>
                      <div className="max-h-36 overflow-y-auto">
                        {lookup.orders.map((o) => (
                          <label
                            key={o.uid}
                            className="flex cursor-pointer items-center gap-2 px-2 py-1 hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={selectedOrders.has(o.uid)}
                              onChange={() => toggleOrder(o.uid)}
                              className="h-3.5 w-3.5"
                            />
                            <span className="truncate font-mono text-xs text-gray-700">{o.platform_code}</span>
                            <span className="text-[10px] text-gray-500">{o.size}</span>
                            {o.cancelled && (
                              <span className="rounded bg-red-100 px-1 text-[10px] text-red-700">cancelled</span>
                            )}
                          </label>
                        ))}
                        {lookup.orders.length === 0 && (
                          <p className="px-2 py-2 text-sm text-gray-500">
                            This pickup holds no orders.
                            {lookup.pickup.cancelled &&
                              ' Cancelling a pickup detaches its orders in core — enter the order UIDs manually.'}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600">Slot (optional)</label>
              <select
                value={slotCode}
                onChange={(e) => setSlotCode(e.target.value as PickupSlotCode | '')}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              >
                <option value="">Let core decide</option>
                {(['S1', 'S2', 'S3', 'S4', 'S5'] as const).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label className="block text-xs font-medium text-gray-600">
                  Reason (required, ≤{OVERRIDE_REASON_MAX})
                </label>
                {/* The limit is core's storage, not a style rule, so show the
                    officer where they are against it rather than silently
                    swallowing keystrokes at the cap. */}
                <span
                  className={`text-xs tabular-nums ${
                    reason.length >= OVERRIDE_REASON_MAX ? 'text-amber-700' : 'text-gray-500'
                  }`}
                >
                  {reason.length}/{OVERRIDE_REASON_MAX}
                </span>
              </div>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={OVERRIDE_REASON_MAX}
                placeholder="Customer requested second same-day pickup"
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            {willMove && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                {orderUids.length === lookup.orders.filter((o) => !o.cancelled).length
                  ? `All active orders will be MOVED out of ${lookup.pickup.platform_code}, leaving it empty.`
                  : `The selected orders will be MOVED out of ${lookup.pickup.platform_code} into the new pickup.`}
              </div>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
            {rejected.length > 0 && (
              <RejectedList orders={rejected} tone="error" heading="Core refused these orders:" />
            )}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button
              onClick={() => mutation.mutate()}
              disabled={!valid || mutation.isPending}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Creating…' : `Create pickup${orderUids.length > 0 ? ` (${orderUids.length})` : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
